/**
 * Display model + real API DTOs for the notification bell.
 *
 * BellNotification is what the component/template work with -- mapped from the API's
 * BellNotificationApiDTO via mapBellApiDtoToBellNotification() below.
 *
 * Backed by ann.NotificationMaster_GetBellNotificationsByUserId (dropdown = unread, "Show All"
 * modal = paginated full history) and ann.NotificationMaster_MarkAsRead (dismiss / Clear All),
 * via NotificationMasterController's GetBellNotificationsByUserId / MarkAsRead endpoints.
 */
export interface BellNotification {
  notificationId: number;
  masterType?: string | null;
  subject: string;
  content: string;
  createdOn: Date;
  attachmentPath?: string | null;
  readStatus: 'Unread' | 'Read';
  /** Total rows matching the current filter across all pages (same value on every row of a
   *  given response) -- lets the "Show All" modal's infinite scroll know when to stop. */
  totalCount: number;
}

/** Raw shape returned by NotificationMasterGetBellNotificationsByUserId (camelCased by ASP.NET). */
export interface BellNotificationApiDTO {
  notificationId: number;
  masterType: string | null;
  nSubject: string;
  nContent: string;
  attachmentPath: string | null;
  createdOn: string;
  readStatus: string;
  totalCount: number;
}

export interface NotificationBellListResponse {
  items: BellNotificationApiDTO[];
}

export interface NotificationMasterGetBellNotificationsByUserIdRequestDTO {
  userId: number;
  tenantId: number;
  tenantBranchId: number;
  /** 'Unread' (dropdown) | null (Show All modal, every status). */
  readStatus: string | null;
  pageNo: number;
  pageSize: number;
}

export interface NotificationMasterMarkAsReadRequestDTO {
  /** Required for push notifications; null for other notification types (see backend SP doc). */
  userId: number | null;
  tenantId: number;
  tenantBranchId: number;
  /** null = "Clear All" for that user; a value = mark just that one row (dismiss). */
  notificationId: number | null;
}

export interface NotificationMarkAsReadResponseDTO {
  rowsUpdated: number;
}

export function mapBellApiDtoToBellNotification(dto: BellNotificationApiDTO): BellNotification {
  return {
    notificationId: dto.notificationId,
    masterType: dto.masterType,
    subject: dto.nSubject,
    content: dto.nContent,
    createdOn: dto.createdOn ? new Date(dto.createdOn) : new Date(),
    attachmentPath: dto.attachmentPath,
    readStatus: dto.readStatus === 'Read' ? 'Read' : 'Unread',
    totalCount: dto.totalCount
  };
}

/** One row per recognizable module. `keywords` are matched as case-insensitive substrings of MasterType. */
export interface ModuleIconConfig {
  keywords: string[];
  icon: string;   // Bootstrap Icons name, without the leading "bi bi-"
  color: string;  // hex color for the icon + its soft background
}

/**
 * Static, global icon/color map by MasterType keyword -- no per-tenant override, per Mihir's
 * call on 2026-09-07 ("no need of this... if it includes purchase or sales or jobworker").
 * Extend this list as more MasterType producers come online; order matters (first match wins).
 */
export const MODULE_ICON_MAP: ModuleIconConfig[] = [
  { keywords: ['purchase'], icon: 'bi-cart-plus', color: '#f1734f' },
  { keywords: ['sale', 'invoice', 'billing'], icon: 'bi-currency-rupee', color: '#0aa699' },
  { keywords: ['jobwork'], icon: 'bi-gear-wide-connected', color: '#727cf5' },
  { keywords: ['supportdesk', 'ticket'], icon: 'bi-life-preserver', color: '#fa5c7c' },
  { keywords: ['hrms', 'staff', 'attendance', 'leave'], icon: 'bi-people-fill', color: '#39afd1' },
  { keywords: ['payroll', 'payslip'], icon: 'bi-cash-stack', color: '#6c757d' },
  { keywords: ['crm', 'lead'], icon: 'bi-person-vcard', color: '#ffbc00' },
  { keywords: ['item', 'inventory', 'stock'], icon: 'bi-box-seam', color: '#0acf97' },
];

/** Shown whenever MasterType is null/blank, or matches nothing above. */
export const DEFAULT_BELL_ICON = 'bi-bell';
export const DEFAULT_BELL_COLOR = '#98a6ad';

/**
 * True for a full URL (http://, https://, or a protocol-relative "//host/...") rather than an
 * in-app route. AttachmentPath (and payload.data.url / the OS notification's click target) is
 * normally an Angular route like "/Sales/Order/123", but nothing stops an AlertConfigMaster /
 * FCM payload from carrying a real external link instead -- and Router.navigateByUrl() has no
 * concept of "external": fed an absolute URL it doesn't recognise the scheme, tries to parse the
 * whole thing as an in-app path, and mangles it (e.g. "http://localhost:4000" opening as
 * "localhost:4200/http:" instead of actually going to localhost:4000). Both
 * NotificationBellComponent.openNotification() and NotificationToastComponent.onToastClick()
 * check this before deciding between router.navigateByUrl() and a real browser navigation.
 */
export function isAbsoluteNotificationLink(path: string): boolean {
  return /^([a-z][a-z0-9+.-]*:)?\/\//i.test(path);
}

export function resolveBellIcon(masterType?: string | null): string {
  const match = resolveModuleConfig(masterType);
  return match ? match.icon : DEFAULT_BELL_ICON;
}

export function resolveBellColor(masterType?: string | null): string {
  const match = resolveModuleConfig(masterType);
  return match ? match.color : DEFAULT_BELL_COLOR;
}

/**
 * MasterType is free text set by whoever configures the alert's DataSourceDef query
 * (ann.AlertsServiceMaster) -- it might be a raw table/entity name ("PurchaseOrderMaster",
 * "tbl_PurchaseOrder", "dbo.SalesInvoice") or a friendlier process label with spaces
 * ("Purchase Order", "Support Desk Ticket"). Stripping every non-alphanumeric character
 * before matching means a keyword like "jobwork" matches "JobWorkOut", "Job Work Out" and
 * "job_work_out" alike, so both styles resolve to the same icon/color.
 */
function normalizeMasterType(masterType: string): string {
  return masterType.toLowerCase().replace(/[^a-z0-9]/g, '');
}

function resolveModuleConfig(masterType?: string | null): ModuleIconConfig | null {
  if (!masterType || !masterType.trim()) { return null; }
  const value = normalizeMasterType(masterType);
  return MODULE_ICON_MAP.find(cfg => cfg.keywords.some(k => value.includes(k))) ?? null;
}

/**
 * "PurchaseOrder" -> "Purchase Order", "tbl_Purchase_Order" -> "Tbl Purchase Order" -- for a
 * friendlier label than the raw MasterType value, whichever of the two naming styles above it
 * was written in.
 */
export function humanizeMasterType(masterType?: string | null): string {
  if (!masterType || !masterType.trim()) { return 'General'; }
  const spaced = masterType
    .replace(/[_\-.]+/g, ' ')                 // table-name style separators -> spaces
    .replace(/([a-z0-9])([A-Z])/g, '$1 $2')   // PascalCase/camelCase -> spaced words
    .replace(/\s+/g, ' ')
    .trim();
  return spaced.replace(/\b\w/g, c => c.toUpperCase());
}

